import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Eye, Send, Loader2, ShieldCheck, Lock, FileText, ClipboardList } from 'lucide-react';
import GigCodeOfConductModal from '../../components/GigCodeOfConductModal';

const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  draft: 'bg-amber-100 text-amber-800 border-amber-200',
  archived: 'bg-slate-100 text-slate-600 border-slate-200',
};

const emptyForm = () => ({
  version_label: '',
  title: 'iEvntür Gig Code of Conduct',
  intro: '',
  footer: '',
  effective_date: new Date().toISOString().slice(0, 10),
  clauses: '',
  checkbox_text: 'I have read and agree to the iEvntür Gig Code of Conduct. I understand I am accepting this booking as an independent contractor, that this Code does not create an employment relationship, and that violations may affect my eligibility for future bookings.',
});

export default function AdminCodeOfConduct() {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // version object for preview modal

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/code-of-conduct/versions');
      setVersions(r.data || []);
    } catch (e) {
      toast.error('Failed to load versions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(emptyForm()); setEditingId(null); setShowForm(true); };
  const startEdit = (v) => {
    setForm({
      version_label: v.version_label,
      title: v.title,
      intro: v.intro || '',
      footer: v.footer || '',
      effective_date: v.effective_date || new Date().toISOString().slice(0, 10),
      clauses: (v.clauses || []).join('\n'),
      checkbox_text: v.checkbox_text,
    });
    setEditingId(v.id);
    setShowForm(true);
  };

  const save = async () => {
    const clauses = form.clauses.split('\n').map((c) => c.trim()).filter(Boolean);
    if (!form.version_label.trim()) { toast.error('Version label is required'); return; }
    if (!clauses.length) { toast.error('Add at least one clause'); return; }
    if (!form.checkbox_text.trim()) { toast.error('Checkbox acknowledgment text is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, clauses };
      if (editingId) await api.put(`/admin/code-of-conduct/versions/${editingId}`, payload);
      else await api.post('/admin/code-of-conduct/versions', payload);
      toast.success(editingId ? 'Draft updated' : 'Draft created');
      setShowForm(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Save failed'));
    } finally { setSaving(false); }
  };

  const publish = async (v) => {
    if (!window.confirm(`Publish ${v.version_label}? It becomes the active policy shown on the next gig acceptance and the current active version is archived. Historical versions and acceptances are preserved.`)) return;
    try {
      await api.post(`/admin/code-of-conduct/versions/${v.id}/publish`);
      toast.success(`${v.version_label} is now the active Code of Conduct`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Publish failed'));
    }
  };

  const previewForm = () => {
    const clauses = form.clauses.split('\n').map((c) => c.trim()).filter(Boolean);
    setPreview({ ...form, clauses });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-coc-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate('/admin/settings')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="coc-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>

        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-600 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Gig Code of Conduct</h1>
              <p className="text-sm text-slate-500">Publish versioned policies. Historical versions stay locked for audit.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/code-of-conduct/acceptances')} data-testid="coc-view-acceptances-btn">
              <ClipboardList className="w-4 h-4 mr-1.5" /> Acceptances
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/client-concerns')} data-testid="coc-view-concerns-btn">
              <ClipboardList className="w-4 h-4 mr-1.5" /> Client Concerns
            </Button>
            <Button className="bg-teal-600 hover:bg-teal-700" onClick={startNew} data-testid="coc-new-version-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Version
            </Button>
          </div>
        </div>

        {showForm && (
          <Card className="mb-6 border-teal-200" data-testid="coc-form">
            <CardContent className="p-5 space-y-4">
              <h2 className="font-semibold text-slate-900">{editingId ? 'Edit Draft' : 'New Version (Draft)'}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600">Version label</label>
                  <Input value={form.version_label} onChange={(e) => setForm({ ...form, version_label: e.target.value })} placeholder="v2-2026-12" data-testid="coc-form-version-label" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">Effective date</label>
                  <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="coc-form-effective-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Title</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="coc-form-title" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Intro (optional)</label>
                <Textarea rows={2} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} data-testid="coc-form-intro" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Clauses — one per line</label>
                <Textarea rows={10} value={form.clauses} onChange={(e) => setForm({ ...form, clauses: e.target.value })} placeholder={'Arrive on time...\nFollow client instructions...'} data-testid="coc-form-clauses" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Required checkbox text</label>
                <Textarea rows={2} value={form.checkbox_text} onChange={(e) => setForm({ ...form, checkbox_text: e.target.value })} data-testid="coc-form-checkbox-text" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Footer / closing statement (optional)</label>
                <Textarea rows={3} value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} placeholder="Independent Contractor Status ..." data-testid="coc-form-footer" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={previewForm} data-testid="coc-form-preview-btn"><Eye className="w-4 h-4 mr-1.5" /> Preview as talent</Button>
                <div className="flex-1" />
                <Button variant="ghost" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
                <Button className="bg-teal-600 hover:bg-teal-700" onClick={save} disabled={saving} data-testid="coc-form-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save Draft
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>
        ) : (
          <div className="space-y-3" data-testid="coc-versions-list">
            {versions.map((v) => (
              <Card key={v.id} data-testid={`coc-version-${v.version_label}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{v.version_label}</span>
                      <Badge className={`text-[10px] border ${STATUS_STYLE[v.status] || ''}`}>{v.status}</Badge>
                      {(v.acceptance_count > 0 || v.status !== 'draft') && <Lock className="w-3.5 h-3.5 text-slate-400" title="Locked for audit" />}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Effective {v.effective_date}
                      {v.published_at ? ` · published ${new Date(v.published_at).toLocaleDateString()}` : ''}
                      {` · ${v.acceptance_count} acceptance${v.acceptance_count === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setPreview(v)} data-testid={`coc-preview-${v.version_label}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                  </Button>
                  {v.status === 'draft' && v.acceptance_count === 0 && (
                    <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`coc-edit-${v.version_label}`}>Edit</Button>
                  )}
                  {v.status !== 'active' && (
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => publish(v)} data-testid={`coc-publish-${v.version_label}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Publish
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <GigCodeOfConductModal
        open={!!preview}
        version={preview}
        readOnly
        heading={preview ? `Preview · ${preview.title || 'Code of Conduct'}` : undefined}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
